import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { VideoCard, fmtViews } from "./VideoCard";
import { X, ExternalLink, Eye, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";

export const VideoPlayerModal = ({ videoId, onClose }) => {
  const [video, setVideo] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setVideo(null);
    setError("");
    api.get(`/videos/${videoId}`).then((r) => setVideo(r.data)).catch(() => setError("Video tidak ditemukan"));
  }, [videoId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: video?.title, url }); return; } catch { /* dibatalkan */ }
    }
    await navigator.clipboard.writeText(url);
    toast.success("Tautan disalin");
  };

  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="video-modal">
      <div className="min-h-full flex items-start justify-center p-4 py-8">
        <div className="card w-full max-w-5xl relative overflow-hidden" onClick={(e) => e.stopPropagation()}>
          <button onClick={onClose} className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 text-slate-300 hover:text-white flex items-center justify-center" data-testid="video-modal-close">
            <X className="w-5 h-5" />
          </button>
          {error && <div className="p-16 text-center text-slate-400" data-testid="video-modal-error">{error}</div>}
          {!video && !error && <div className="p-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-cyan1" /></div>}
          {video && (
            <>
              <div className={`bg-black mx-auto ${video.is_short ? "aspect-[9/16] max-h-[80vh]" : "aspect-video"}`}>
                <iframe
                  src={`${video.embed_url}${video.platform === "youtube" ? "?autoplay=1&rel=0" : ""}`}
                  title={video.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="video-iframe"
                />
              </div>
              <div className="p-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="chip chip-cyan">{video.category}</span>
                  {video.is_short && <span className="chip chip-magenta">Shorts</span>}
                  <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
                    {video.channel_name} · {new Date(video.published_at).toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}
                    {video.views > 0 && <> · <Eye className="w-3 h-3 inline" /> {fmtViews(video.views)}</>}
                  </span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-black" data-testid="video-modal-title">{video.title}</h2>
                {video.description && <p className="text-slate-400 text-sm mt-3 whitespace-pre-line line-clamp-4">{video.description}</p>}
                <div className="flex flex-wrap gap-3 mt-5">
                  <a href={video.url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="video-open-original"><ExternalLink className="w-4 h-4" /> Buka di {video.platform === "youtube" ? "YouTube" : video.platform === "tiktok" ? "TikTok" : "Instagram"}</a>
                  <button onClick={share} className="btn-ghost" data-testid="video-share"><Share2 className="w-4 h-4" /> Bagikan</button>
                </div>
                {video.related?.length > 0 && (
                  <div className="mt-8">
                    <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">Video terkait · {video.category}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="video-related">
                      {video.related.slice(0, 3).map((v) => (
                        <div key={v.id} onClick={(e) => { e.preventDefault(); navigate(`/videos/${v.id}`); }}>
                          <VideoCard video={v} compact />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
